<template>
    <BasePanel 
        headline="lang_ERP_INVOICES_CAPTION_INVOICEROWS"
        :alvqa="alvqa"
    >            
        <template v-slot:panelContent>
            <v-data-table
                class="baseInlineEditingDataTable"
                :headers="headers"
                :items="items"
                :items-per-page="990"
                :header-props="{ sortIcon: '' }"
                hide-default-footer
                :loading="isLoading"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
            >
                <template v-slot:top>
                    <v-row class="pb-3">
                        <v-col cols="6">
                            <v-card elevation="0">
                                    <v-card-text>
                                        <div class="read-only">
                                            {{ $t('lang_ERP_INVOICES_CAPTION_QUANTITY') }}: {{ $n(getTotalQuantity(), "numberInteger") }} |
                                            {{ $t('lang_GENERIC_CAPTION_SUM') }} {{ $t('lang_ERP_INVOICES_CAPTION_SUM_NET') }}: {{ $n(getTotalNetSum(), "number") }} |
                                            {{ $t('lang_GENERIC_CAPTION_SUM') }} {{ $t('lang_ERP_INVOICES_CAPTION_SUM_GROSS') }}: {{ $n(getTotalBrutto(), "number" ) }}
                                        </div>
                                    </v-card-text>
                            </v-card>
                        </v-col>
                        
                        <v-col 
                            align-self="center"
                            cols="3"
                        >
                            <BaseButton 
                                v-if="showImportExcelButton"
                                @click="importExcel"
                                label="lang_GENERAL_CAPTION_IMPORT_EXCEL"
                                block
                                :alvqa="alvqa + '-button-import-excel'"
                                :disabled="isImportButtonDisabled"
                            />
                        </v-col>

                        <v-col cols="3" align-self="center">
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

                <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

                <!-- Items -->
                <template v-slot:[`item.title`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-' + item.id + '-text-title'">
                        <BaseIcon  
                            :icon="$alvPlugin.getIconOfType(item.type)" 
                            :tooltipText="$alvPlugin.getIconTooltipText(item)"
                            :alvqa="alvqa + '-icon'"
                        />
                        {{ getTitle(item) }}
                    </div>
                </template>

                <template v-slot:[`item.productId`]="{ item }">
                    {{ $alvPlugin.getProductItemId(item) }}
                </template> 

                <template v-slot:[`item.batch`]="{ item }">
                    {{ $t(getLangOfBatch(item.batch)) }}
                </template>  

                <template v-slot:[`item.price`]="{ item }">
                    <div v-if="item.price != null">
                        <div>{{$alvPlugin.formatNumberToFourDecimals($alvPlugin.stringToNumber(item.price))}}</div>
                    </div>
                </template>

                <template v-slot:[`item.sum`]="{ item }">
                    <i18n-n v-if="item.sum != null" :value="$alvPlugin.stringToNumber(autocalculateSum(item))" format="number"></i18n-n>                    
                </template>

                <template v-slot:[`item.vat`]="{ item }">
                    <i18n-n v-if="item.vat != null" :value="$alvPlugin.stringToNumber(item.vat)" format="number"></i18n-n>
                </template>

                <!-- Row Button: DELETE  -->
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteInvoiceRows(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-' + item.id + '-icon-delete'"
                    />
                </template>

                <!-- Row Button: EDIT -->
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-' + item.id + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseInlineEditingDataTable from "@/components/_base/BaseInlineEditingDataTable"
import BaseButton from '@/components/_buttons/BaseButton'
import { DropdownErpShippingBatches } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "InvoiceRowsEditPanel",  
    components: { 
        BasePanel,
        BaseIcon,
        BaseInlineEditingDataTable,
        BaseButton
    },
    data() {
        return {
            alvqa: 'erp-invoices-edit-form-panel-invoicerows'
        }
    },
    computed: {
        items() {  return this.$store.getters.getInvoicesInvoiceRows },
         isLoading() { return this.$store.getters.getIsLoading },
		/** Returns an Array with header-objects with props.
         *  @return {[{text: String, align: string, value: string}]} props of the header */
        headers() {
            return [
                {   text: this.$t('lang_ERP_INVOICES_CAPTION_ITEM_TITLE'), 
                    alvqa: this.alvqa + "header-item-title",
                    align: "left", 
                    sortable: false, 
                    value: "title", 
                    tooltip: "", 
                    class: 'text-no-wrap'},

                {   text: this.$t('lang_GENERIC_CAPTION_ID'), 
                    alvqa: this.alvqa + "header-id",
                    align: "left", 
                    sortable: false, 
                    value: "productId", 
                    tooltip:"", 
                    width: "150px"}, // needs at least 150px to show ALVID

                {   text: this.$t('lang_ERP_SHIPPING_CAPTION_BATCH'), 
                    alvqa: this.alvqa + "header-batch",
                    align: "left", 
                    sortable: false, 
                    value: "batch", 
                    tooltip:"", 
                    width: "80px"},

                {   text: this.$t('lang_ERP_INVOICES_CAPTION_QUANTITY'), 
                    alvqa: this.alvqa + "header-quantity",
                    align: "right",
                    sortable: false,
                    value: "quantity",
                    tooltip:"",
                    width: "80px"},

                {   text: this.$t('lang_ERP_INVOICES_CAPTION_ITEM_PRICE') + ' ' + this.$store.getters.getInvoicesDetailsCurrency,
                    alvqa: this.alvqa + "header-item-price",
                    align: "right",
                    sortable: false,
                    value: "price",
                    tooltip:"",
                    width: "100px"},

                {   text: this.$t('lang_ERP_INVOICES_CAPTION_SUM_NET') + ' ' + this.$store.getters.getInvoicesDetailsCurrency,
                    alvqa: this.alvqa + "header-sum-net",
                    align: "right",
                    sortable: false,
                    value: "sum",
                    tooltip:"",
                    width: "100px"},

                {   text: this.$t('lang_GENERIC_CAPTION_VAT') + ' %', 
                    alvqa: this.alvqa + "header-vat", 
                    align: "right",
                    sortable: false,
                    value: "vat",
                    tooltip:"",
                    width: "80px"},
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        showImportExcelButton() {
            // let isLabelCompanySelected = this.$store.getters.getSelectedOverAllCompany.context === 2
            // let isInvoicesSaved = this.$store.getters.getInvoicesDetailsAlvId !== 'ALV000000000IN'
            return this.$store.getters.getSelectedOverAllCompany.context === 2
            return isLabelCompanySelected && isInvoicesSaved
        },
        isImportButtonDisabled() { return this.$store.getters.getInvoicesDetailsAlvId === 'ALV000000000IN' }
    },
    methods: {
        getTitle(item) {
            if(typeof item.title === "string") return item.title
            else return item.title.title
        },
        autocalculateSum(item) {
            let quantity = item.quantity;
            if((item.type == 'label/freetext' || item.type == 'publishing/freetext') && quantity == null){
                quantity = 1;
            }
            item.sum = this.$alvPlugin.stringToNumber(item.price) * this.$alvPlugin.stringToNumber(quantity)
            return item.sum
        },
        addNewRow(){
            let companyType = this.$store.getters.getSelectedOverAllCompany.context
            let searchType  = companyType == 2 ? "label/releases" : "publishing/works"
            this.$store.commit('setInvoicesInvoiceRowsTitleSearch', searchType)
            this.$store.commit('setInvoicesInvoiceRowsIsNewSearch', true) // adding a new row
            this.$store.commit('setInvoicesInvoiceRowsTitleOptions', [])
            this.$store.commit('setShowEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))
            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultInvoicesInvoiceRows)) 
            this.$store.commit('setInvoicesInvoiceRowsEditingDialogDetails', defaultValues)
        }, 
        getLangOfBatch(selection) {
            if (selection) {
                return DropdownErpShippingBatches.find(obj => {
                    return obj.value == selection
                }).text
            } else return ""
        },
        deleteInvoiceRows(item){ 
            // set the selected share/row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedInvoicesInvoiceRows", [item.id])
            this.$store.commit('setShowDeletionDialog', true)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedInvoicesInvoiceRows', item.id)
            this.$store.commit('setInvoicesInvoiceRowsIsNewSearch', false) // editing an existing invoice row
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))
            // formatting numbers to show in correct notation, with comma or dot => $n()
            // also: we need to check whether they are null, because $n() cannot handle "null" => converts to 0,00
            if (detailsDeepCopy['price'] != null) {
                detailsDeepCopy['price'] = this.$alvPlugin.formatNumberToFourDecimals(this.$alvPlugin.stringToNumber(detailsDeepCopy['price']))
            }
            if (detailsDeepCopy['vat'] != null)
                detailsDeepCopy['vat'] = this.$n(this.$alvPlugin.stringToNumber(detailsDeepCopy['vat']), 'number')

            // Backend returns search type such as "label/releases" as type for each invoice row
            this.$store.commit('setInvoicesInvoiceRowsTitleSearch', item.type)
            this.$store.commit('setInvoicesInvoiceRowsTitleOptions', [detailsDeepCopy])
            this.$store.commit('setInvoicesInvoiceRowsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowEditingDialog', true) 
        },

        // Import
        importExcel() {
            this.$store.commit('setSelectedFileImport', 'invoiceRows')
            this.$store.dispatch('doFileImport')
        },

        getTotalQuantity() { return this.items.reduce((prev, cur) => prev + this.$alvPlugin.stringToNumber(cur.quantity), 0) },
        getTotalNetSum() { return this.items.reduce((prev, cur) => prev + this.$alvPlugin.stringToNumber(cur.sum), 0) },
        getTotalBrutto() {
            let sum = 0
            sum = this.items.reduce((prev, cur) => {
                    let currentGross = 0
                    if(cur.vat !== null) {
                        currentGross = cur.sum * (cur.vat/100+1)
                    } else {
                        currentGross = cur.sum
                    }
                    return prev + this.$alvPlugin.stringToNumber(currentGross)
                }, 0
            )
            return sum
        },
    }
}
</script>

<style>
  .baseInlineEditingDataTable table {
    table-layout: fixed;
  }
  .baseInlineEditingDataTable table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>